import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { motion, type Variants } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const MotionBox = motion.create(Box);

// ── Tokens ──────────────────────────────────────────────────
const NAVY = '#0F1B3D';
const BLUE = '#2563EB';
const BLUE_LIGHT = '#DBEAFE';
const BLUE_XLIGHT = '#EFF6FF';
const GRAY_50 = '#F8FAFC';
const GRAY_100 = '#F1F5F9';
const GRAY_200 = '#E2E8F0';
const GRAY_400 = '#94A3B8';
const GRAY_600 = '#475569';
const GRAY_800 = '#1E293B';
const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const SHADOW_LG = '0 12px 40px rgba(15,27,61,.12), 0 4px 12px rgba(15,27,61,.06)';
const SHADOW_BLUE = '0 8px 32px rgba(37,99,235,.18)';

const FADE_UP: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (d: number = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: d, ease: [0.4, 0, 0.2, 1] },
  }),
};

// ── Input styles ─────────────────────────────────────────────
const inputSx = {
  width: '100%', display: 'block',
  bgcolor: GRAY_50, border: `1.5px solid ${GRAY_200}`,
  borderRadius: '10px', color: NAVY,
  fontSize: '14px', fontFamily: FONT, outline: 'none',
  padding: '11px 14px',
  transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
  '&:focus': { borderColor: BLUE, background: '#fff', boxShadow: '0 0 0 3px rgba(37,99,235,.1)' },
  '&::placeholder': { color: GRAY_400 },
} as const;

const selectSx = {
  ...inputSx,
  appearance: 'none', WebkitAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394A3B8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
  paddingRight: '36px', cursor: 'pointer',
} as const;

// ── States list ───────────────────────────────────────────────
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

// ── Form types ────────────────────────────────────────────────
type FormValues = {
  schoolName: string;
  students: string;
  city: string;
  state: string;
  fullName: string;
  email: string;
  mobile: string;
  usingErp: 'yes' | 'no' | '';
  demoDatetime: string;
  requirements: string;
};

// ── Field label ───────────────────────────────────────────────
function FL({ label, required }: { label: string; required?: boolean }) {
  return (
    <Typography component="label" sx={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: GRAY_800, mb: '7px', fontFamily: FONT }}>
      {label}
      {required && <Box component="span" sx={{ color: BLUE, ml: '3px' }}>*</Box>}
    </Typography>
  );
}

function Err({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <Typography sx={{ fontSize: '0.72rem', color: '#EF4444', mt: '4px', fontFamily: FONT }}>{msg}</Typography>;
}

// ── Nav ───────────────────────────────────────────────────────
function DemoNav() {
  return (
    <Box component="nav" sx={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${GRAY_100}` }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: '20px', md: '48px' }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700, fontSize: '1.15rem', color: NAVY, letterSpacing: '-0.02em', fontFamily: FONT }}>
          <Box sx={{ width: 32, height: 32, background: `linear-gradient(135deg, ${BLUE} 0%, #1D4ED8 100%)`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', color: 'white', fontWeight: 800 }}>S</Box>
          SkolarMinds
        </Box>
      </Box>
    </Box>
  );
}

// ── Main form ─────────────────────────────────────────────────
function BookingForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, control, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: { schoolName: '', students: '', city: '', state: '', fullName: '', email: '', mobile: '', usingErp: '', demoDatetime: '', requirements: '' },
  });

  const requirements = watch('requirements', '');

  const minDatetime = (() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 60);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  const onSubmit = async (_data: FormValues) => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 1600));
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: '48px' }}>
        <Box sx={{ width: 64, height: 64, bgcolor: '#D1FAE5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', mx: 'auto', mb: '16px' }}>✅</Box>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: NAVY, mb: '8px', fontFamily: FONT }}>Demo Booked Successfully!</Typography>
        <Typography sx={{ fontSize: '0.95rem', color: GRAY_600, fontFamily: FONT }}>Our team will confirm within 2 business hours. Check your inbox for a calendar invite.</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

      {/* School / College Name */}
      <Box>
        <FL label="School / College Name" required />
        <Box component="input" type="text" placeholder="Enter institution name"
          {...register('schoolName', { required: 'Please enter your institution name.' })}
          sx={{ ...inputSx, borderColor: errors.schoolName ? 'rgba(239,68,68,0.5)' : GRAY_200 }}
        />
        <Err msg={errors.schoolName?.message} />
      </Box>

      {/* Number of Students */}
      <Box>
        <FL label="Number of Students" required />
        <Controller name="students" control={control} rules={{ required: 'Please select a student range.' }}
          render={({ field }) => (
            <Box component="select" value={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name}
              sx={{ ...selectSx, borderColor: errors.students ? 'rgba(239,68,68,0.5)' : GRAY_200 }}>
              <option value="" disabled>Select student count</option>
              <option value="100-500">100 – 500</option>
              <option value="500-1000">500 – 1,000</option>
              <option value="1000-2000">1,000 – 2,000</option>
              <option value="2000+">2,000+</option>
            </Box>
          )}
        />
        <Err msg={errors.students?.message} />
      </Box>

      {/* City + State */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        <Box>
          <FL label="City" required />
          <Box component="input" type="text" placeholder="Enter city"
            {...register('city', { required: 'Please enter your city.' })}
            sx={{ ...inputSx, borderColor: errors.city ? 'rgba(239,68,68,0.5)' : GRAY_200 }}
          />
          <Err msg={errors.city?.message} />
        </Box>
        <Box>
          <FL label="State" required />
          <Controller name="state" control={control} rules={{ required: 'Please select your state.' }}
            render={({ field }) => (
              <Box component="select" value={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name}
                sx={{ ...selectSx, borderColor: errors.state ? 'rgba(239,68,68,0.5)' : GRAY_200 }}>
                <option value="" disabled>Select state</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </Box>
            )}
          />
          <Err msg={errors.state?.message} />
        </Box>
      </Box>

      {/* Full Name */}
      <Box>
        <FL label="Full Name" required />
        <Box component="input" type="text" placeholder="Enter your full name"
          {...register('fullName', { required: 'Please enter your full name.' })}
          sx={{ ...inputSx, borderColor: errors.fullName ? 'rgba(239,68,68,0.5)' : GRAY_200 }}
        />
        <Err msg={errors.fullName?.message} />
      </Box>

      {/* Email */}
      <Box>
        <FL label="Email Address" required />
        <Box component="input" type="email" placeholder="name@school.com"
          {...register('email', { required: 'Please enter a valid email.', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, message: 'Please enter a valid email.' } })}
          sx={{ ...inputSx, borderColor: errors.email ? 'rgba(239,68,68,0.5)' : GRAY_200 }}
        />
        <Err msg={errors.email?.message} />
      </Box>

      {/* Mobile */}
      <Box>
        <FL label="Mobile Number" required />
        <Box component="input" type="tel" placeholder="Enter mobile number" maxLength={10}
          {...register('mobile', { required: 'Please enter a valid mobile number.', pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number.' } })}
          sx={{ ...inputSx, borderColor: errors.mobile ? 'rgba(239,68,68,0.5)' : GRAY_200 }}
        />
        <Err msg={errors.mobile?.message} />
      </Box>

      {/* Using Other ERP? */}
      <Box>
        <FL label="Using Other ERP?" required />
        <Controller name="usingErp" control={control} rules={{ required: 'Please select an option.' }}
          render={({ field }) => (
            <Box sx={{ display: 'flex', gap: '12px' }}>
              {(['yes', 'no'] as const).map(v => {
                const active = field.value === v;
                return (
                  <Box key={v} onClick={() => field.onChange(v)} sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', px: '20px', py: '11px', border: '1.5px solid', borderColor: active ? BLUE : GRAY_200, borderRadius: '10px', bgcolor: active ? BLUE_XLIGHT : GRAY_50, transition: 'border-color 0.2s, background 0.2s', userSelect: 'none' }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${active ? BLUE : GRAY_400}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BLUE }} />}
                    </Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: NAVY, fontFamily: FONT }}>{v === 'yes' ? 'Yes' : 'No'}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        />
        <Err msg={errors.usingErp?.message} />
      </Box>

      {/* Preferred Demo Date & Time */}
      <Box>
        <FL label="Preferred Demo Date & Time" required />
        <Box component="input" type="datetime-local" min={minDatetime}
          {...register('demoDatetime', { required: 'Please select a date and time.', validate: v => !v || new Date(v) > new Date() || 'Please select a future date.' })}
          sx={{ ...inputSx, borderColor: errors.demoDatetime ? 'rgba(239,68,68,0.5)' : GRAY_200 }}
        />
        <Err msg={errors.demoDatetime?.message} />
      </Box>

      {/* Requirements */}
      <Box>
        <FL label="Tell us about your requirements" />
        <Box
          component="textarea"
          placeholder="Tell us about your institution, challenges, modules you're interested in, or any specific requirements..."
          maxLength={500}
          {...register('requirements')}
          sx={{ ...inputSx, resize: 'vertical', minHeight: '100px' }}
        />
        <Typography sx={{ textAlign: 'right', fontSize: '0.72rem', color: requirements.length > 450 ? '#F59E0B' : GRAY_400, mt: '4px', fontFamily: FONT }}>
          {requirements.length} / 500
        </Typography>
      </Box>

      {/* Submit */}
      <Button type="submit" fullWidth disabled={loading} sx={{ py: '15px', bgcolor: BLUE, color: '#fff', fontSize: '1rem', fontWeight: 700, borderRadius: '12px', boxShadow: SHADOW_BLUE, fontFamily: FONT, textTransform: 'none', display: 'flex', alignItems: 'center', gap: '10px', mt: '4px', transition: 'all 0.22s', '&:hover:not(:disabled)': { bgcolor: '#1D4ED8', transform: 'translateY(-2px)', boxShadow: '0 16px 48px rgba(37,99,235,.28)' }, '&:disabled': { opacity: 0.65, bgcolor: BLUE, color: '#fff' } }}>
        {loading ? <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.8)' }} /> : <>Book My Demo <ArrowRight size={18} /></>}
      </Button>

    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────
export default function BookDemoPage() {
  return (
    <Box sx={{ fontFamily: FONT, bgcolor: GRAY_50, minHeight: '100vh' }}>
      <DemoNav />

      <Box sx={{ maxWidth: '560px', mx: 'auto', px: { xs: '16px', sm: '24px' }, py: { xs: '40px', md: '60px' } }}>
        <MotionBox initial="hidden" animate="visible" variants={FADE_UP} custom={0}
          sx={{ bgcolor: '#fff', borderRadius: '20px', boxShadow: SHADOW_LG, border: `1px solid ${GRAY_200}`, p: { xs: '28px 20px', sm: '36px 32px' } }}
        >
          {/* Header */}
          <Box sx={{ mb: '28px' }}>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: NAVY, letterSpacing: '-0.03em', mb: '6px', fontFamily: FONT }}>
              Book Your Free Demo
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: GRAY_600, fontFamily: FONT }}>
              Typically responds within 2 business hours.
            </Typography>
          </Box>

          <BookingForm />
        </MotionBox>
      </Box>

    </Box>
  );
}
